<script setup lang="ts">
import Capsule from '@/components/common/Capsule.vue'
import { useRouter } from 'vue-router'
import { OutlinedDownIcon } from '@/components/icons'

interface IProps {
  isMain?: boolean
  number?: string
  title?: string
  bullets?: string[]
  captions?: {
    rank?: string
    text: string
  }[]
  path?: string
  color?: string
  showGlow?: boolean
}

const props = defineProps<IProps>()

const router = useRouter()

const handleClick = () => {
  if (props.path) {
    router.push(props.path)
  }
}
</script>

<template>
  <Capsule
    :isMain="props.isMain"
    mode="static"
    :childrenCount="1"
    :background="props.isMain ? 'dark' : 'light'"
    :glow="props.showGlow"
  >
    <template #1>
      <div class="value-outer">
        <div class="value-inner">
          <div class="logo">
            <slot name="logo" />
          </div>
          <div class="header" :style="{ color: color }">
            <div class="number" :class="{ main: isMain }">{{ number }}</div>
            <div class="title" :class="{ main: isMain }">
              <span>{{ title }}</span>
              <slot name="icon" />
            </div>
            <div class="line" :class="{ main: isMain }"></div>
          </div>
          <div class="bullets" v-if="props.bullets?.length">
            <div v-for="(bullet, index) in props.bullets" :key="index">&bull; {{ bullet }}</div>
          </div>
          <div class="captions" :style="{ color: color }" v-if="props.captions?.length">
            <div class="caption-item" v-for="(caption, index) in props.captions" :key="index">
              <span v-if="caption.rank" class="rank">{{ caption.rank }}</span>
              <span class="text">{{ caption.text }}</span>
            </div>
          </div>
          <div @click="handleClick" class="caption">
            <OutlinedDownIcon /><span>View details</span>
          </div>
        </div>
      </div>
    </template>
  </Capsule>
</template>

<style scoped>
.value-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  height: 100%;
  position: relative;
  padding-block: 10%;
}

.value-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  padding: 1.25rem;
}

.logo {
  flex: 0.15;
  display: flex;
  align-items: center;
}

.header {
  display: flex;
  flex-direction: column;
  align-items: start;
  font-size: var(--font-title);
  font-weight: 600;
  flex: 0.1;
  width: 100%;
}

.header .number.main {
  color: var(--color-text);
}

.header .title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-title-3);
  line-height: var(--font-title-3);
  text-align: start;
  font-weight: 600;
  position: relative;
  width: 100%;
}

.header .title span {
  max-width: 70%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
}

.header .title.main span {
  font-size: var(--font-title-2);
  line-height: var(--font-title-2);
  font-weight: 900;
}

.header .line {
  width: 100%;
  height: 0.0625rem;
  background-color: var(--color-text);
  margin-block: 0.625rem;
}

.header .line.main {
  background-color: var(--color-background-bright);
}

.bullets {
  display: flex;
  flex-direction: column;
  align-items: start;
  text-align: start;
  width: 100%;
  flex: 0.35;
  font-size: var(--font-size-body);
}

.captions {
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  gap: 0.625rem;
  flex: 0.55;
}

.captions .caption-item {
  display: flex;
  gap: 0.3125rem;
}

.captions .caption-item .rank {
  font-size: 1.875rem;
  line-height: 1.75rem;
  font-weight: 600;
  position: relative;
}

.captions .caption-item .text {
  font-size: 1rem;
  line-height: 1rem;
  text-align: start;
  font-weight: 600;
  text-transform: uppercase;
}

.captions .caption-item .rank::before {
  content: '#';
  font-size: 1.125rem;
  font-weight: 600;
  position: absolute;
  left: -0.625rem;
  top: -0.15625rem;
}

.caption {
  font-size: 0.6875rem;
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  cursor: pointer;
  flex: 0.2;
}
</style>
